import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  ClipboardList,
  CreditCard,
  FileText,
  Gauge,
  LayoutDashboard,
  LogOut,
  MoreHorizontal,
  Package,
  Receipt,
  ScrollText,
  Settings,
  ShieldCheck,
  Sparkles,
  Tags,
  Users,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import { NAV_MODULES, type PermissionKey } from "@laundrypro/shared";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/BrandingContext";
import { BottomSheet } from "../components/ui";
import { BrandLogo } from "../components/BrandLogo";

/**
 * Icons for the navigation, keyed to the shared module catalog.
 *
 * Every icon here is direction-neutral — no chevrons, no arrows. That is
 * deliberate: an interface with no directional icons has no RTL mirroring
 * problem to solve, rather than a mirroring problem solved correctly.
 */
const MODULE_ICONS: Record<string, LucideIcon> = {
  dashboard: LayoutDashboard,
  orders: ClipboardList,
  customers: Users,
  services: Sparkles,
  pricing: Tags,
  payments: CreditCard,
  invoices: Receipt,
  expenses: Wallet,
  inventory: Package,
  reports: Gauge,
  users: Users,
  roles: ShieldCheck,
  settings: Settings,
  branding: Sparkles,
  tax_settings: FileText,
  audit_logs: ScrollText,
};

/** How many destinations fit in the mobile tab bar before overflowing to "More". */
const MOBILE_TAB_LIMIT = 4;

export function AppShell() {
  const { user, permissions, logout } = useAuth();
  const { appName, language, branding } = useBranding();
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);

  const ar = language === "ar";

  if (!user) return null;

  // The single filter. Everything below — sidebar, tabs, sheet — derives from
  // this one list, so a permission change cannot leave one surface out of step
  // with another.
  const visible = NAV_MODULES.filter((module) =>
    permissions.has(module.viewPermission as PermissionKey),
  );

  const tabs = visible.slice(0, MOBILE_TAB_LIMIT);
  const overflow = visible.slice(MOBILE_TAB_LIMIT);

  const labelFor = (module: (typeof NAV_MODULES)[number]) => (ar ? module.labelAr : module.labelEn);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="flex min-h-dvh bg-background text-foreground">
      {/*
        Desktop sidebar. Not fixed and not sticky — it stays on screen because
        <main> is the scroll container, and `overflow-y-auto` on a flex item
        resolves its automatic min-height to 0 so the column never grows.

        `border-e` rather than `border-r`, so the edge follows the reading
        direction.
      */}
      <aside className="hidden w-64 shrink-0 flex-col border-e border-white/[0.06] bg-sidebar text-white md:flex">
        <div className="flex items-center gap-2.5 px-5 py-[1.375rem]">
          <BrandLogo size={32} className="max-h-8 shrink-0" />
          <span className="truncate text-lg font-semibold tracking-tight">{appName}</span>
        </div>
        <div className="mx-4 mb-1 h-px bg-white/[0.06]" />

        <nav className="flex-1 space-y-1 px-3 py-2" aria-label={ar ? "التنقل" : "Navigation"}>
          {visible.map((module) => {
            const Icon = MODULE_ICONS[module.key] ?? LayoutDashboard;
            return (
              <NavLink
                key={module.key}
                to={module.route}
                className={({ isActive }) =>
                  `group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-all duration-150 ${
                    isActive
                      ? "bg-white/[0.07] font-medium text-accent-300"
                      : "text-muted-subtle hover:bg-white/[0.04] hover:text-ink-100"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {/* `start-0` not `left-0` — the accent bar sits on the
                        reading-start edge in both directions. */}
                    <span
                      className={`absolute inset-y-1.5 start-0 w-0.5 rounded-full bg-accent-400 transition-opacity duration-150 ${
                        isActive ? "opacity-100" : "opacity-0"
                      }`}
                    />
                    <Icon
                      className={`size-[18px] shrink-0 transition-colors ${
                        isActive ? "text-accent-300" : "text-muted-foreground group-hover:text-ink-300"
                      }`}
                      strokeWidth={1.75}
                      aria-hidden="true"
                    />
                    {labelFor(module)}
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="border-t border-white/[0.06] px-4 py-4">
          <div className="mb-3 flex items-center gap-2.5">
            <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-accent-500/15 text-xs font-semibold text-accent-300 ring-1 ring-inset ring-accent-400/20">
              {user.fullName.slice(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-white">{user.fullName}</div>
              <div className="truncate text-[11px] text-muted-subtle">
                {user.roles.map((r) => (ar ? r.nameAr : r.nameEn)).join("، ") || "—"}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-xs text-muted-subtle transition-colors hover:bg-white/[0.05] hover:text-white"
          >
            <LogOut className="size-3.5" strokeWidth={1.75} aria-hidden="true" />
            {ar ? "تسجيل الخروج" : "Sign out"}
          </button>
          {/* No footer branding text here. It lives on the authenticated
              branding payload, and `useBranding` deliberately carries only the
              PUBLIC subset — widening that so the sidebar could show a footer
              line would also expose it to anyone who loads the login screen. */}
        </div>
      </aside>

      {/*
        `min-w-0` is load-bearing. As a flex item this column defaults to
        `min-width: auto`, meaning it refuses to shrink below its widest
        descendant — and one of those descendants is a 210mm (794px) A4 invoice
        preview. Measured at a 375px viewport the whole page grew to 868px and
        scrolled sideways, dragging the header and tab bar off-screen with it.
        The preview already sits in its own `overflow-x-auto` box; that box
        never got the chance to scroll because its ancestor had already grown
        to fit it. Any wide content anywhere in the app hits the same trap.
      */}
      <div className="flex min-h-dvh min-w-0 flex-1 flex-col">
        {/* Mobile top bar. The safe-area inset is folded into the padding so the
            brand clears a notch instead of sitting under it. */}
        <header className="flex items-center justify-between border-b border-line bg-card px-4 pb-3 pt-[calc(0.75rem+env(safe-area-inset-top))] md:hidden">
          <div className="flex min-w-0 items-center gap-2">
            <BrandLogo size={28} className="max-h-7 shrink-0" />
            <span className="truncate text-base font-semibold tracking-tight text-ink-900">{appName}</span>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            aria-label={ar ? "تسجيل الخروج" : "Sign out"}
            className="flex size-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-ink-100"
          >
            <LogOut className="size-[18px]" strokeWidth={1.75} aria-hidden="true" />
          </button>
        </header>

        {/* The scroll container. Its bottom padding clears the fixed tab bar,
            so the last row of a table is never trapped underneath it. */}
        <main className="flex-1 overflow-y-auto p-4 pb-[calc(5.5rem+env(safe-area-inset-bottom))] md:p-8 md:pb-8">
          <Outlet />
        </main>

        {/*
          Mobile navigation is a bottom tab bar, not a collapsed sidebar and not
          a hamburger drawer. Destinations stay visible and within thumb reach
          instead of being hidden behind a menu that has to be opened first.
        */}
        <nav
          className="fixed inset-x-0 bottom-0 z-30 flex border-t border-line bg-card/90 pb-[env(safe-area-inset-bottom)] backdrop-blur-md md:hidden"
          aria-label={ar ? "التنقل" : "Navigation"}
        >
          {tabs.map((module) => {
            const Icon = MODULE_ICONS[module.key] ?? LayoutDashboard;
            return (
              <NavLink
                key={module.key}
                to={module.route}
                className={({ isActive }) =>
                  `relative flex flex-1 flex-col items-center gap-1 py-2.5 text-[11px] font-medium transition-colors ${
                    isActive ? "text-accent-600" : "text-muted-subtle"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={`absolute inset-x-6 top-0 h-0.5 rounded-full bg-accent-500 transition-opacity ${
                        isActive ? "opacity-100" : "opacity-0"
                      }`}
                    />
                    <Icon className="size-5" strokeWidth={isActive ? 2 : 1.75} aria-hidden="true" />
                    {labelFor(module)}
                  </>
                )}
              </NavLink>
            );
          })}

          {/* Only rendered when there is actually an overflow — a role with four
              or fewer destinations gets no dead "More" button. */}
          {overflow.length > 0 && (
            <button
              type="button"
              onClick={() => setMoreOpen(true)}
              className="flex flex-1 flex-col items-center gap-1 py-2.5 text-[11px] font-medium text-muted-subtle transition-colors hover:text-ink-600"
            >
              <MoreHorizontal className="size-5" strokeWidth={1.75} aria-hidden="true" />
              {ar ? "المزيد" : "More"}
            </button>
          )}
        </nav>

        <BottomSheet
          open={moreOpen}
          onClose={() => setMoreOpen(false)}
          title={ar ? "المزيد" : "More"}
          closeLabel={ar ? "إغلاق" : "Close"}
        >
          {/* A grid of tiles rather than a list: the sheet holds destinations,
              and a grid shows more of them without scrolling. */}
          <div className="grid grid-cols-3 gap-3">
            {overflow.map((module) => {
              const Icon = MODULE_ICONS[module.key] ?? LayoutDashboard;
              return (
                <NavLink
                  key={module.key}
                  to={module.route}
                  onClick={() => setMoreOpen(false)}
                  className="flex flex-col items-center gap-1.5 rounded-xl border border-line-hairline py-3 text-center text-[11px] text-ink-600"
                >
                  <Icon className="size-5 text-muted-foreground" strokeWidth={1.75} aria-hidden="true" />
                  {labelFor(module)}
                </NavLink>
              );
            })}
          </div>
        </BottomSheet>
      </div>
    </div>
  );
}
